import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Menu, X, Calendar, UtensilsCrossed, Sparkles, Mail, BookOpen } from 'lucide-react';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';

// Brand logo URL (transparent background)
const BRAND_LOGO = "https://customer-assets.emergentagent.com/job_17ae6403-2082-451f-978f-8e823488d32d/artifacts/bd5nly0b_tastes%20good%20does%20good%20%20group%20logo%20%20%287%29.png";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 w-full z-50 glass-nav shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center group" data-testid="nav-logo">
            <img 
              src={BRAND_LOGO} 
              alt="Cakery Wonderland" 
              className="h-10 w-auto group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-white hover:text-primary font-medium transition-colors" data-testid="nav-home-link">
              Home
            </Link>
            <Link to="/catering" className="text-white hover:text-primary font-medium transition-colors flex items-center gap-1" data-testid="nav-catering-link">
              <UtensilsCrossed className="w-4 h-4" />
              Book Catering
            </Link>
            <Link to="/subscriptions" className="text-white hover:text-primary font-medium transition-colors flex items-center gap-1" data-testid="nav-subscriptions-link">
              <Sparkles className="w-4 h-4" />
              Buy a Subscription
            </Link>
            <Link to="/experiences" className="text-white hover:text-primary font-medium transition-colors flex items-center gap-1" data-testid="nav-experiences-link">
              <Calendar className="w-4 h-4" />
              Book Experiences
            </Link>
            <Link to="/blog" className="text-white hover:text-primary font-medium transition-colors flex items-center gap-1" data-testid="nav-blog-link">
              <BookOpen className="w-4 h-4" />
              Blog
            </Link>
            <Link to="/contact" className="text-white hover:text-primary font-medium transition-colors" data-testid="nav-contact-link">
              Contact
            </Link>

            {user ? (
              <div className="flex items-center space-x-3">
                {user.is_admin && (
                  <Link to="/admin" data-testid="nav-admin-link">
                    <Button variant="ghost" className="text-primary hover:text-primary hover:bg-primary/10 font-medium" data-testid="admin-button">
                      Admin
                    </Button>
                  </Link>
                )}
                <Link to="/dashboard" data-testid="nav-dashboard-link">
                  <Button variant="ghost" className="text-white hover:text-primary hover:bg-white/5" data-testid="dashboard-button">
                    <User className="w-5 h-5 mr-2" />
                    {user.name}
                  </Button>
                </Link>
                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="rounded-full border-white/20 text-white hover:bg-white/10 hover:border-primary"
                  data-testid="logout-button"
                >
                  Logout
                </Button>
              </div>
            ) : (
              <Link to="/login" data-testid="nav-login-link">
                <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-6 shadow-lg shadow-primary/50 button-primary" data-testid="login-button">
                  Login
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              className="text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-paper border-t border-white/10" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            <Link to="/" className="block text-white hover:text-primary font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-home-link">
              Home
            </Link>
            <Link to="/catering" className="block text-white hover:text-primary font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-catering-link">
              <UtensilsCrossed className="w-4 h-4" />
              Book Catering
            </Link>
            <Link to="/subscriptions" className="block text-white hover:text-primary font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-subscriptions-link">
              <Sparkles className="w-4 h-4" />
              Buy a Subscription
            </Link>
            <Link to="/experiences" className="block text-white hover:text-primary font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-experiences-link">
              <Calendar className="w-4 h-4" />
              Book Experiences
            </Link>
            <Link to="/blog" className="block text-white hover:text-primary font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-blog-link">
              <BookOpen className="w-4 h-4" />
              Blog
            </Link>
            <Link to="/contact" className="block text-white hover:text-primary font-medium flex items-center gap-2" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-contact-link">
              <Mail className="w-4 h-4" />
              Contact
            </Link>
            {user ? (
              <>
                {user.is_admin && (
                  <Link to="/admin" className="block text-primary hover:text-primary-hover font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-admin-link">
                    Admin
                  </Link>
                )}
                <Link to="/dashboard" className="block text-white hover:text-primary font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-dashboard-link">
                  Dashboard
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left text-white hover:text-primary font-medium"
                  data-testid="mobile-logout-button"
                >
                  Logout
                </button>
              </>
            ) : (
              <Link to="/login" className="block text-white hover:text-primary font-medium" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-login-link">
                Login
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
